/**
 * Dispatcher de slash commands en el chat de análisis (DF7-12).
 *
 * Comandos:
 *   /help                    — listado inline.
 *   /status                  — estado de los 3 módulos del pipeline.
 *   /history [incident_id]   — runs recientes (filtra por incident si aplica).
 *   /approve <id>            — aprueba una pendiente.
 *   /reject <id> [razón…]    — rechaza una pendiente.
 *   /analyze <host|incident> — solo válido en página proactiva (no chat).
 *
 * Las respuestas a comandos client-side (help, status, history) NO se
 * persisten en la sesión: viven en state local del componente. Solo se
 * persiste el comando en sí (vía useSendMessage type=command), para
 * trazabilidad operativa.
 */

import { useCallback } from "react";

import { ApiError, api } from "@/api/client";
import type {
  PlaybookRunList,
  SystemHealthResponse,
} from "@/api/types";
import { useApprove, useReject } from "@/hooks/useApprovals";

export interface CommandResponse {
  ok: boolean;
  text: string;
  timestamp: string;
}

const HELP_TEXT = `Comandos disponibles:
  /help                       este listado
  /status                     estado de ingester / correlator / SOAR
  /history [incident_id]      últimos runs de protocolos
  /approve <approval_id>      aprobar una acción pendiente
  /reject <id> [razón]        rechazar (la razón alimenta a AROC)
`;

export function useCommandDispatcher(opts: { incidentId?: string | null }) {
  const approveMutation = useApprove();
  const rejectMutation = useReject();
  const incidentIdHint = opts.incidentId ?? null;

  return useCallback(
    async (command: string, args: string[]): Promise<CommandResponse> => {
      const ts = new Date().toISOString();
      try {
        switch (command) {
          case "help":
            return ok(HELP_TEXT, ts);
          case "status":
            return await runStatus(ts);
          case "history":
            return await runHistory(args[0] || incidentIdHint, ts);
          case "approve": {
            const id = args[0];
            if (!id) return fail("Uso: /approve <approval_id>", ts);
            await approveMutation.mutateAsync(id);
            return ok(`Aprobada #${id}.`, ts);
          }
          case "reject": {
            const id = args[0];
            if (!id) return fail("Uso: /reject <approval_id> [razón]", ts);
            const reason = args.slice(1).join(" ").trim() || undefined;
            await rejectMutation.mutateAsync({ id, reason });
            return ok(`Rechazada #${id}${reason ? ` — ${reason}` : ""}.`, ts);
          }
          case "analyze":
            return fail(
              "El comando /analyze se usa fuera del chat. Abre 'Nuevo análisis' o el botón 'Análisis profundo' del incidente.",
              ts,
            );
          default:
            return fail(`Comando desconocido: /${command}. Prueba /help`, ts);
        }
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? `${err.code}: ${err.message}`
            : err instanceof Error
              ? err.message
              : "Error desconocido.";
        return fail(msg, ts);
      }
    },
    [approveMutation, rejectMutation, incidentIdHint],
  );
}

function ok(text: string, ts: string): CommandResponse {
  return { ok: true, text, timestamp: ts };
}

function fail(text: string, ts: string): CommandResponse {
  return { ok: false, text, timestamp: ts };
}

async function runStatus(ts: string): Promise<CommandResponse> {
  const data = await api<SystemHealthResponse>("/api/v1/health/system");
  const lines = data.modules
    .map((m) => `  ${m.name.padEnd(11)} ${m.up ? "UP " : m.up === false ? "DOWN" : "?"}${m.error ? ` (${m.error})` : ""}`)
    .join("\n");
  return ok(`Pipeline:\n${lines}\nMódulo análisis: ${data.analysis_enabled ? "ON" : "off"}`, ts);
}

async function runHistory(
  incidentId: string | null | undefined,
  ts: string,
): Promise<CommandResponse> {
  const url = incidentId
    ? `/api/v1/playbook-runs?incident_id=${encodeURIComponent(incidentId)}&per_page=10`
    : "/api/v1/playbook-runs?per_page=10";
  const data = await api<PlaybookRunList>(url);
  if (data.items.length === 0) return ok("Sin runs.", ts);
  const lines = data.items
    .map(
      (r) =>
        `  ${r.run_id.slice(0, 8)}  ${r.playbook_id.padEnd(45)} ${r.mode.padEnd(7)} ${r.status}`,
    )
    .join("\n");
  return ok(`Runs recientes (${data.items.length}/${data.total}):\n${lines}`, ts);
}
